/**
 * Horas laborables (Europe/Madrid) alineadas con backend fechaLaborable defaults.
 * O(días): intersección por ventanas, sin bucle minuto×Intl (evita OOM en /tareas).
 */

const TZ = "Europe/Madrid";
const DOW_MAP = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

export const DEFAULT_OFFICE = {
  morningStart: 9 * 60 + 30,
  morningEnd: 14 * 60,
  afternoonStart: 16 * 60 + 30,
  afternoonEnd: 19 * 60,
};

const dateFmt = new Intl.DateTimeFormat("en-CA", {
  timeZone: TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const timeFmt = new Intl.DateTimeFormat("en-GB", {
  timeZone: TZ,
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});
const dowFmt = new Intl.DateTimeFormat("en-US", {
  timeZone: TZ,
  weekday: "short",
});

export function madridParts(ms) {
  const d = new Date(ms);
  const ymd = dateFmt.format(d);
  const [hh, mi] = timeFmt.format(d).split(":").map((x) => parseInt(x, 10));
  const dow = DOW_MAP[dowFmt.format(d)] ?? 0;
  return { ymd, hour: hh, minute: mi, dow };
}

function officeWindows(office = DEFAULT_OFFICE) {
  const windows = [];
  if (office.morningEnd > office.morningStart) {
    windows.push([office.morningStart, office.morningEnd]);
  }
  if (office.afternoonEnd > office.afternoonStart) {
    windows.push([office.afternoonStart, office.afternoonEnd]);
  }
  return windows;
}

function nextYmd(ymd) {
  const d = new Date(`${ymd}T12:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

function compareYmd(a, b) {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/** Convierte ymd Madrid + minutos-desde-medianoche → epoch ms (respetando DST). */
function madridWallToUtcMs(ymd, minutesOfDay) {
  const hh = Math.floor(minutesOfDay / 60);
  const mi = minutesOfDay % 60;
  const pad = (n) => String(n).padStart(2, "0");
  const local = `${ymd}T${pad(hh)}:${pad(mi)}:00`;
  // Europe/Madrid: CET +01 / CEST +02 — elegir el que redondea al wall clock pedido.
  for (const off of ["+02:00", "+01:00"]) {
    const ms = Date.parse(local + off);
    if (!Number.isFinite(ms)) continue;
    const p = madridParts(ms);
    if (p.ymd === ymd && p.hour * 60 + p.minute === minutesOfDay) return ms;
  }
  return Date.parse(local + "+01:00");
}

function weekdayOfYmd(ymd) {
  // Mediodía Madrid ≈ 11:00Z invierno / 10:00Z verano; +01/+02 a las 12:00 local.
  const ms = madridWallToUtcMs(ymd, 12 * 60);
  return madridParts(ms).dow;
}

/**
 * Minutos laborables en [fromMs, toMs) por intersección de ventanas de oficina.
 * Máx. ~90 días de recorrido (cap) para no congelar el tablero.
 */
export function countLaborableMinutesBetween(fromMs, toMs, office = DEFAULT_OFFICE) {
  const start = Number(fromMs);
  const end = Number(toMs);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;

  const capEnd = Math.min(end, start + 90 * 86400000);
  const windows = officeWindows(office);
  if (!windows.length) return 0;

  let day = madridParts(start).ymd;
  const endYmd = madridParts(capEnd).ymd;
  let total = 0;
  let guard = 0;

  while (compareYmd(day, endYmd) <= 0 && guard < 100) {
    guard += 1;
    const dow = weekdayOfYmd(day);
    if (dow >= 1 && dow <= 5) {
      for (const [ws, we] of windows) {
        const winStart = madridWallToUtcMs(day, ws);
        const winEnd = madridWallToUtcMs(day, we);
        const a = Math.max(start, winStart);
        const b = Math.min(capEnd, winEnd);
        if (b > a) total += Math.floor((b - a) / 60000);
      }
    }
    if (day === endYmd) break;
    day = nextYmd(day);
  }
  return total;
}

export function countLaborableHoursCeil(fromMs, toMs, office = DEFAULT_OFFICE) {
  const mins = countLaborableMinutesBetween(fromMs, toMs, office);
  return Math.max(1, Math.ceil(mins / 60));
}

export function countLaborableHoursElapsed(fromMs, nowMs, office = DEFAULT_OFFICE) {
  const mins = countLaborableMinutesBetween(fromMs, nowMs, office);
  return Math.floor(mins / 60);
}

export function esMinutoLaborable(ms, office = DEFAULT_OFFICE) {
  const parts = madridParts(ms);
  if (parts.dow < 1 || parts.dow > 5) return false;
  const m = parts.hour * 60 + parts.minute;
  return officeWindows(office).some(([s, e]) => m >= s && m < e);
}
